/**
 * Tags settings tab - label management for holdings and watchlist
 */
import { useState } from 'react'
import {
  useTags,
  useCreateTag,
  useUpdateTag,
  useDeleteTag,
} from '../../hooks'
import { Button, Input, Modal, LoadingCard } from '../common'
import type { Tag, TagCreate, TagUpdate } from '../../types'

// Predefined colors for tags
const TAG_COLORS = [
  { name: 'Green', value: '#10B981' },
  { name: 'Blue', value: '#3B82F6' },
  { name: 'Purple', value: '#8B5CF6' },
  { name: 'Orange', value: '#F59E0B' },
  { name: 'Red', value: '#EF4444' },
  { name: 'Pink', value: '#EC4899' },
  { name: 'Indigo', value: '#6366F1' },
  { name: 'Teal', value: '#14B8A6' },
  { name: 'Yellow', value: '#EAB308' },
  { name: 'Gray', value: '#6B7280' },
]

export function TagsTab() {
  const { data: tags, isLoading } = useTags()
  const createTag = useCreateTag()
  const updateTag = useUpdateTag()
  const deleteTag = useDeleteTag()

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingTag, setEditingTag] = useState<Tag | null>(null)
  const [formData, setFormData] = useState<TagCreate>({
    name: '',
    description: '',
    color: TAG_COLORS[0].value,
  })
  const [error, setError] = useState('')

  const openCreateModal = () => {
    setEditingTag(null)
    setFormData({
      name: '',
      description: '',
      color: TAG_COLORS[0].value,
    })
    setError('')
    setIsModalOpen(true)
  }

  const openEditModal = (tag: Tag) => {
    setEditingTag(tag)
    setFormData({
      name: tag.name,
      description: tag.description || '',
      color: tag.color || TAG_COLORS[0].value,
    })
    setError('')
    setIsModalOpen(true)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    try {
      if (editingTag) {
        const update: TagUpdate = {
          name: formData.name,
          description: formData.description || null,
          color: formData.color || null,
        }
        await updateTag.mutateAsync({
          tagId: editingTag.tag_id,
          data: update,
        })
      } else {
        await createTag.mutateAsync(formData)
      }
      setIsModalOpen(false)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Operation failed')
    }
  }

  const handleDelete = async (tagId: string, name: string) => {
    if (!confirm(`Are you sure you want to delete the "${name}" tag? It will be removed from all holdings and watchlist items.`)) {
      return
    }

    try {
      await deleteTag.mutateAsync(tagId)
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to delete tag')
    }
  }

  if (isLoading) {
    return <LoadingCard />
  }

  // Sort tags by name
  const sortedTags = [...(tags || [])].sort((a, b) => a.name.localeCompare(b.name))

  return (
    <div className="space-y-6">
      {/* Tags List */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <div>
            <h2 className="text-lg font-medium text-gray-900">Tags</h2>
            <p className="mt-1 text-sm text-gray-500">
              Create labels to categorize holdings and watchlist items
            </p>
          </div>
          <Button onClick={openCreateModal}>Create Tag</Button>
        </div>

        {sortedTags.length === 0 ? (
          <p className="px-6 py-8 text-center text-gray-500">
            No tags yet. Create one to categorize your investments.
          </p>
        ) : (
          <div className="divide-y divide-gray-200">
            {sortedTags.map((tag) => (
              <div
                key={tag.tag_id}
                className="px-6 py-4 flex items-center justify-between hover:bg-gray-50"
              >
                <div className="flex items-center space-x-4">
                  <span
                    className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium"
                    style={{
                      backgroundColor: `${tag.color || '#6B7280'}20`,
                      color: tag.color || '#6B7280',
                      border: `1px solid ${tag.color || '#6B7280'}40`,
                    }}
                  >
                    {tag.name}
                  </span>
                  {tag.description && (
                    <span className="text-sm text-gray-500 hidden sm:inline">
                      {tag.description}
                    </span>
                  )}
                </div>

                <div className="flex items-center space-x-4">
                  <div className="text-sm text-gray-500 flex space-x-4">
                    <span title="Holdings with this tag">
                      {tag.holdings_count || 0} holdings
                    </span>
                    <span title="Watchlist items with this tag">
                      {tag.watchlist_count || 0} watchlist
                    </span>
                  </div>
                  <div className="flex space-x-1">
                    <button
                      onClick={() => openEditModal(tag)}
                      className="text-gray-400 hover:text-gray-600 p-1"
                      title="Edit"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                      </svg>
                    </button>
                    <button
                      onClick={() => handleDelete(tag.tag_id, tag.name)}
                      className="text-gray-400 hover:text-red-600 p-1"
                      title="Delete"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Quick Stats */}
      {sortedTags.length > 0 && (
        <div className="bg-gray-50 rounded-lg p-4 text-sm text-gray-600">
          <strong>{sortedTags.length}</strong> tags total |{' '}
          <strong>{sortedTags.reduce((sum, t) => sum + (t.holdings_count || 0), 0)}</strong> tag assignments on holdings |{' '}
          <strong>{sortedTags.reduce((sum, t) => sum + (t.watchlist_count || 0), 0)}</strong> on watchlist
        </div>
      )}

      {/* Create/Edit Tag Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={editingTag ? 'Edit Tag' : 'Create Tag'}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            label="Name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="e.g., quantum, dividend, momentum"
            required
          />

          <Input
            label="Description (optional)"
            value={formData.description || ''}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder="What does this tag represent?"
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Color
            </label>
            <div className="flex flex-wrap gap-2">
              {TAG_COLORS.map((color) => (
                <button
                  key={color.value}
                  type="button"
                  className={`w-8 h-8 rounded-full border-2 ${
                    formData.color === color.value
                      ? 'border-gray-900 ring-2 ring-offset-2 ring-gray-900'
                      : 'border-transparent'
                  }`}
                  style={{ backgroundColor: color.value }}
                  onClick={() => setFormData({ ...formData, color: color.value })}
                  title={color.name}
                />
              ))}
            </div>
          </div>

          {/* Preview */}
          {formData.name && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Preview
              </label>
              <span
                className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium"
                style={{
                  backgroundColor: `${formData.color || '#6B7280'}20`,
                  color: formData.color || '#6B7280',
                  border: `1px solid ${formData.color || '#6B7280'}40`,
                }}
              >
                {formData.name}
              </span>
            </div>
          )}

          {error && <p className="text-sm text-red-600">{error}</p>}

          <div className="flex justify-end space-x-3 pt-4">
            <Button
              type="button"
              variant="secondary"
              onClick={() => setIsModalOpen(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              isLoading={createTag.isPending || updateTag.isPending}
            >
              {editingTag ? 'Save Changes' : 'Create Tag'}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  )
}
